import CancelOutlined from '@mui/icons-material/CancelOutlined';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { unitKeys } from 'api/queries/UnitQueries';
import { SectionButton } from 'library/components/controls/buttons/SectionButton';
import { BaseDialog } from 'library/components/controls/dialogs/BaseDialog';
import { ControlledTextField } from 'library/components/controls/form/ControlledTextField';
import { useDialogState } from 'library/hooks/useDialogState';

interface AnnulmentFormData {
	description: string;
}

interface AnnulmentServiceAgreementButtonProps {
	unitId: number;
	agreementId: number;
	onSuccess?: () => void;
}

export const AnnulmentServiceAgreementButton = ({
	unitId,
	agreementId,
	onSuccess,
}: AnnulmentServiceAgreementButtonProps): React.JSX.Element => {
	const { t } = useTranslation(['unitOverview', 'commonButton', 'common']);
	const requiredMessage = t('common:validation-error-message');
	const { enqueueSnackbar } = useSnackbar();
	const queryClient = useQueryClient();
	const { api } = useProductCenterApi();

	const {
		open: isAnnulmentDialogOpen,
		handleClose: handleCloseAnnulmentDialog,
		handleOpen: handleOpenAnnulmentDialog,
	} = useDialogState(false);

	const {
		control,
		handleSubmit,
		reset,
		formState: { isValid },
	} = useForm<AnnulmentFormData>({
		defaultValues: { description: '' },
		mode: 'onChange',
	});

	const annulmentMutation = useMutation({
		mutationFn: ({ description }: AnnulmentFormData) =>
			api.annulServiceAgreement(unitId.toString(), agreementId, {
				description,
			}),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: unitKeys.activeServiceAgreementData(unitId),
				}),
				queryClient.invalidateQueries({
					queryKey: unitKeys.serviceAgreementData(unitId),
				}),
			]);
			enqueueSnackbar(
				t('unitOverview:service-agreement-annulment-success'),
				{ variant: 'success' }
			);
			handleCloseAnnulmentDialog();
			reset();
			if (onSuccess) {
				onSuccess();
			}
		},
		onError: () => {
			enqueueSnackbar(
				t('unitOverview:service-agreement-annulment-error'),
				{ variant: 'error' }
			);
		},
	});

	const onSubmit = (data: AnnulmentFormData) => {
		annulmentMutation.mutate(data);
	};

	const handleClose = () => {
		handleCloseAnnulmentDialog();
		reset();
	};

	return (
		<>
			<SectionButton
				text={t('unitOverview:service-agreement-annulment-title')}
				icon={<CancelOutlined />}
				onClick={handleOpenAnnulmentDialog}
			/>
			<BaseDialog
				title={t('unitOverview:service-agreement-annulment-title')}
				open={isAnnulmentDialogOpen}
				onClose={handleClose}
				onConfirm={handleSubmit(onSubmit)}
				confirmButtonText={t('commonButton:yes')}
				closeButtonText={t('commonButton:no')}
				isSubmitting={annulmentMutation.isPending}
				disabled={!isValid}>
				<Typography variant="body1" sx={{ mb: 2 }}>
					{t('unitOverview:service-agreement-annulment-confirm')}
				</Typography>
				<ControlledTextField
					name="description"
					control={control}
					label={t(
						'unitOverview:service-agreement-annulment-description'
					)}
					validation={{
						required: requiredMessage,
						validate: (value: string) =>
							value.trim().length > 0 || requiredMessage,
					}}
					multiline
					minRows={3}
					fullWidth
					required
				/>
			</BaseDialog>
		</>
	);
};
